/**
* FleshCash Affiliate Tracker
*
* Persists affiliate data in cookies and syncs it to Shopify cart attributes
* so order webhooks can handle attribution server-side.
*/
(() => {
  if (window.__ilfFleshCashAffiliateTrackerLoaded) return;
  window.__ilfFleshCashAffiliateTrackerLoaded = true;

  /* ── Config ────────────────────────────────────────────────── */
  // 90-day window to match Impact Radius' cookie horizon, so multi-session
  // conversions (click now, buy weeks later) are attributed the way IR
  // attributes them — see docs/may-earnings-pipeline-handoff.md §7.
  const COOKIE_DAYS = 90;
  const MAX_VALUE_LENGTH = 128;
  const VALUE_ALLOWLIST = /[^a-zA-Z0-9_.-]/g;

  const COOKIES = {
    ref: 'fc_aff',
    click: 'fc_click',
    sub: 'fc_sub',
  };

  const PARAM_ALIASES = {
    ref: ['ref', 'aff', 'affid'],
    click: ['fc_click'],
    sub: ['subid', 'sub_id', 'sub1'],
  };

  // Phase 1: current keys (compatible with existing webhook).
  const CART_KEYS = {
    ref: 'ref',
    click: 'fc_click',
    sub: 'subid',
  };
  // Phase 2: uncomment once webhook supports hidden underscore keys.
  // const CART_KEYS = { ref: '_ref', click: '_fc_click', sub: '_subid' };

  /* ── Helpers ───────────────────────────────────────────────── */
  const sanitize = (value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    const cleaned = trimmed
      .slice(0, MAX_VALUE_LENGTH)
      .replace(VALUE_ALLOWLIST, '');

    return cleaned || null;
  };

  const setCookie = (name, value, days) => {
    const expiresAt = new Date();
    expiresAt.setTime(expiresAt.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = [
      `${name}=${encodeURIComponent(value)}`,
      `expires=${expiresAt.toUTCString()}`,
      'path=/',
      'SameSite=Lax',
      'Secure',
    ].join(';');
  };

  const getCookie = (name) => {
    const prefix = `${name}=`;
    const cookie = document.cookie
      .split(';')
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(prefix));

    if (!cookie) return null;
    return decodeURIComponent(cookie.slice(prefix.length));
  };

  const readFirstParam = (params, aliases) => {
    for (const alias of aliases) {
      const value = sanitize(params.get(alias));
      if (value) return value;
    }
    return null;
  };

  const normalizeAttributeValue = (value) => (
    value == null
      ? ''
      : String(value)
  );

  /* ── Core ──────────────────────────────────────────────────── */
  const resolveTrackingData = () => {
    const params = new URLSearchParams(window.location.search);

    const urlValues = {
      ref: readFirstParam(params, PARAM_ALIASES.ref),
      click: readFirstParam(params, PARAM_ALIASES.click),
      sub: readFirstParam(params, PARAM_ALIASES.sub),
    };

    let cookieValues = {
      ref: sanitize(getCookie(COOKIES.ref)),
      click: sanitize(getCookie(COOKIES.click)),
      sub: sanitize(getCookie(COOKIES.sub)),
    };

    if (urlValues.ref) {
      setCookie(COOKIES.ref, urlValues.ref, COOKIE_DAYS);
      cookieValues.ref = urlValues.ref;
    }

    if (urlValues.click) {
      setCookie(COOKIES.click, urlValues.click, COOKIE_DAYS);
      cookieValues.click = urlValues.click;
    }

    if (urlValues.sub) {
      setCookie(COOKIES.sub, urlValues.sub, COOKIE_DAYS);
      cookieValues.sub = urlValues.sub;
    }

    return cookieValues;
  };

  const buildCartAttributes = (trackingData) => {
    const attributes = {};

    if (trackingData.ref) attributes[CART_KEYS.ref] = trackingData.ref;
    if (trackingData.click) attributes[CART_KEYS.click] = trackingData.click;
    if (trackingData.sub) attributes[CART_KEYS.sub] = trackingData.sub;

    return attributes;
  };

  const syncCartAttributes = async (trackingData) => {
    const nextAttributes = buildCartAttributes(trackingData);
    if (!Object.keys(nextAttributes).length) return;

    try {
      const cartResponse = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const existingAttributes = cart?.attributes ?? {};
      console.log('[ILF FleshCash] Current Shopify cart attributes:', existingAttributes);
      const hasChanges = Object.entries(nextAttributes).some(
        ([key, value]) => normalizeAttributeValue(existingAttributes[key]) !== value
      );

      if (!hasChanges) return;

      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes: nextAttributes }),
      });

      if (!updateResponse.ok) return;
    } catch (_error) {
      // Best-effort sync: attribution should never interrupt storefront behavior.
    }
  };

  /* ── Form attribute injection ──────────────────────────────────
   * "Buy It Now" / Shop Pay / accelerated-checkout flows skip the
   * /cart page entirely — they POST the product form straight to
   * /cart/add with the `checkout` action, so the async POST to
   * /cart/update.js never has a chance to land before the redirect.
   *
   * Shopify's product form, however, persists any `attributes[...]`
   * hidden inputs into the cart on `/cart/add`. Injecting them
   * directly into every cart-bound form means accelerated checkout
   * keeps attribution without waiting on a network round-trip.
   *
   * We also keep `syncCartAttributes` for the classic Add-to-Cart →
   * /cart → Checkout path so themes that mutate the cart token
   * (e.g. drawers) still get attrs persisted server-side. */

  const ATTR_INPUT_MARKER = 'data-ilf-fleshcash-attr';
  const FORM_SELECTOR = 'form[action*="/cart/add"], form[action="/cart"]';

  const cartFormFromTarget = (target) => {
    if (!(target instanceof HTMLFormElement)) return null;
    const action = target.getAttribute('action') || '';
    if (!action) return null;
    if (action.includes('/cart/add') || /\/cart(?:\?|$)/.test(action)) return target;
    return null;
  };

  const injectFormAttributes = (form, trackingData) => {
    if (!(form instanceof HTMLFormElement)) return;

    const pairs = [];
    if (trackingData.ref) pairs.push([CART_KEYS.ref, trackingData.ref]);
    if (trackingData.click) pairs.push([CART_KEYS.click, trackingData.click]);
    if (trackingData.sub) pairs.push([CART_KEYS.sub, trackingData.sub]);
    if (!pairs.length) return;

    for (const [key, value] of pairs) {
      const name = `attributes[${key}]`;
      // Reuse an existing input we own; otherwise create one. We never
      // touch attribute inputs that aren't tagged with our marker so a
      // theme that already sets `attributes[ref]` for its own purposes
      // wins (this should not happen in our themes but is defensive).
      let input = form.querySelector(
        `input[${ATTR_INPUT_MARKER}][name="${name}"]`,
      );
      if (!input) {
        const existing = form.querySelector(`input[name="${name}"]`);
        if (existing && !existing.hasAttribute(ATTR_INPUT_MARKER)) continue;
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute(ATTR_INPUT_MARKER, '1');
        form.appendChild(input);
      }
      if (input.value !== value) input.value = value;
    }
  };

  const injectAllCartForms = (trackingData) => {
    document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
      injectFormAttributes(form, trackingData);
    });
  };

  /* ── Dynamic-checkout interception ────────────────────────────
   * "More payment options" (and themes that mount equivalent links)
   * is a same-origin <a> whose click handler creates a brand-new
   * checkout from the current variant — entirely bypassing the cart
   * the user is currently looking at. The new checkout therefore
   * never sees our `attributes[ref]` hidden inputs.
   *
   * Route around it: pre-populate the cart via /cart/add.js with the
   * full product form (so our attributes hitch a ride), then send the
   * user to /checkout. Shopify's standard checkout still surfaces
   * Shop Pay, PayPal, Apple Pay etc. in its express column, so the
   * UX is equivalent for the shopper but attribution is preserved.
   *
   * NOTE: the PayPal/Shop Pay/Apple Pay "Buy with X" buttons live in
   * a cross-origin iframe (PayPal SDK / Shopify Accelerated Checkout)
   * — we cannot listen for clicks inside them. Those flows still
   * require a server-side IP+timestamp fallback in the webhook for
   * full coverage. */

  const DYNAMIC_CHECKOUT_SELECTOR =
    'a#more-payment-options-link, a.shopify-payment-button__more-options';
  const DYNAMIC_CHECKOUT_GUARD = 'data-ilf-fleshcash-handled';

  const handleDynamicCheckoutClick = async (event, link) => {
    const form = link.closest('form[action*="/cart/add"]');
    if (!form) return;

    const variant = form.querySelector('[name="id"]');
    if (!variant || !variant.value) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const fd = new FormData(form);
    try {
      const res = await fetch(form.action + '.js', {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('cart/add failed');
      window.location.href = '/checkout';
    } catch (_err) {
      // Cart add failed — let the original button do its thing rather
      // than leaving the user stranded. Mark the link as handled to
      // avoid our listener firing recursively.
      link.setAttribute(DYNAMIC_CHECKOUT_GUARD, '1');
      link.click();
      link.removeAttribute(DYNAMIC_CHECKOUT_GUARD);
    }
  };

  const watchDynamicCheckout = () => {
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest(DYNAMIC_CHECKOUT_SELECTOR);
        if (!link) return;
        if (link.hasAttribute(DYNAMIC_CHECKOUT_GUARD)) return;
        handleDynamicCheckoutClick(event, link);
      },
      true,
    );
  };

  /* ── Express-checkout wrapper pre-population ───────────────────
   * Cross-origin iframes (PayPal SDK, Shopify Accelerated Checkout
   * for Shop Pay / Apple Pay / Google Pay) cannot be intercepted on
   * click — the click happens inside the iframe and never reaches
   * page JS. The express checkout SDK then builds an ephemeral
   * checkout from the product variant alone, dropping our form
   * `attributes[ref]` inputs on the floor.
   *
   * Mitigation: when the user shows intent (hover or focus on the
   * express-button wrapper) we pre-populate the real cart with the
   * currently-selected variant via /cart/add.js. The cart already
   * carries our affiliate attributes (set by syncCartAttributes).
   * Some express SDKs notice the cart already contains the variant
   * and reuse it; even when they don't, the order still surfaces in
   * Shopify with the cart attributes attached for webhook attribution
   * via a future server-side fallback.
   *
   * Trade-offs accepted by the maintainer:
   *   - The cart icon will reflect the pre-added item; an affiliate
   *     visitor who hovers and walks away has the product in cart.
   *   - Variant changes after pre-population are not re-synced; the
   *     pre-added variant is whatever was selected at hover time.
   *     Shoppers can edit the variant in cart/checkout. */

  const EXPRESS_WRAPPER_SELECTOR = '.shopify-payment-button, shopify-accelerated-checkout';
  const EXPRESS_GUARD = 'data-ilf-fleshcash-preload';

  const preloadCartFromExpressWrapper = async (wrapper) => {
    if (wrapper.getAttribute(EXPRESS_GUARD) === '1') return;
    wrapper.setAttribute(EXPRESS_GUARD, '1');

    const form = wrapper.closest('form[action*="/cart/add"]');
    if (!form) return;

    const variant = form.querySelector('[name="id"]');
    if (!variant || !variant.value) return;

    // Skip if the current variant is already in the cart — this is the
    // common case for users who Add-to-Cart then hover the express
    // button, and avoids producing duplicate line items.
    try {
      const cart = await fetch('/cart.js', {
        method: 'GET',
        headers: { Accept: 'application/json' },
      }).then((r) => (r.ok ? r.json() : null));
      const variantId = String(variant.value);
      if (cart?.items?.some((it) => String(it.id) === variantId)) return;
    } catch (_err) {
      // If we can't read the cart, fall through and try the add anyway
      // — a duplicate line is a smaller failure than missed attribution.
    }

    const fd = new FormData(form);
    try {
      await fetch(form.action + '.js', {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/json' },
      });
    } catch (_err) {
      // Best-effort: a failed pre-add must never block the express
      // button the shopper is about to click.
    }
  };

  const attachExpressWrapperListeners = (wrapper) => {
    if (wrapper.dataset.ilfFleshcashListening === '1') return;
    wrapper.dataset.ilfFleshcashListening = '1';

    // `mouseenter` doesn't bubble, so we attach per-element. `focusin`
    // covers keyboard navigation onto the wrapper or its descendants.
    const handler = () => preloadCartFromExpressWrapper(wrapper);
    wrapper.addEventListener('mouseenter', handler);
    wrapper.addEventListener('focusin', handler);
    wrapper.addEventListener('touchstart', handler, { passive: true });
  };

  const watchExpressWrappers = () => {
    document.querySelectorAll(EXPRESS_WRAPPER_SELECTOR).forEach(attachExpressWrapperListeners);
    if (typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches?.(EXPRESS_WRAPPER_SELECTOR)) {
            attachExpressWrapperListeners(node);
            continue;
          }
          node.querySelectorAll?.(EXPRESS_WRAPPER_SELECTOR).forEach(attachExpressWrapperListeners);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  const watchForCartForms = (trackingData) => {
    // Themes that swap the product form on variant change (or render it
    // lazily after hydration) won't have the form in the DOM at init.
    // A MutationObserver re-injects whenever a matching form appears.
    if (typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          const form = cartFormFromTarget(node);
          if (form) {
            injectFormAttributes(form, trackingData);
            continue;
          }
          node.querySelectorAll?.(FORM_SELECTOR).forEach((f) => {
            injectFormAttributes(f, trackingData);
          });
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    // Final safety net: capturing-phase submit handler injects attrs the
    // instant before the form leaves the page, even if the form was
    // created and submitted synchronously between observer ticks.
    document.addEventListener(
      'submit',
      (event) => {
        const form = cartFormFromTarget(event.target);
        if (form) injectFormAttributes(form, trackingData);
      },
      true,
    );
  };

  /* ── Extensibility Hook ────────────────────────────────────── */
  const onAffiliateVisit = (_trackingData) => {
    // Reserved for optional pageview/click integrations in the future.
  };

  /* ── Entry Point ───────────────────────────────────────────── */
  const init = async () => {
    const trackingData = resolveTrackingData();
    if (!trackingData.ref && !trackingData.click && !trackingData.sub) return;

    onAffiliateVisit(trackingData);
    injectAllCartForms(trackingData);
    watchForCartForms(trackingData);
    watchDynamicCheckout();
    watchExpressWrappers();
    await syncCartAttributes(trackingData);
  };

  void init();
})();
